import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { conceptBySlug, concepts } from "@/content/en/concepts";
import type { Concept } from "@/content/en/types";
import { ideaBySlug } from "@/content/en/ideas";
import { researchBySlug } from "@/content/en/research";
import { CitationBox, BriefingCTA, TopicList } from "@/components/en/EnPrimitives";
import { MachineSummary } from "@/components/en/MachineSummary";
import {
  personSchema,
  breadcrumbSchema,
  enHreflangLinks,
  PERSON_ID,
  EN_HOME,
  SITE_URL,
} from "@/lib/en-schema";
import { pageTitle } from "@/lib/brand";
import { pilotIdForConcept } from "@/lib/lexicon-pilot";


export const Route = createFileRoute("/concepts/$slug")({
  loader: ({ params }) => {
    const concept = conceptBySlug(params.slug);
    if (!concept) throw notFound();
    return { concept };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: pageTitle("Concept not found", "en") }, { name: "robots", content: "noindex" }] };
    }
    const c = loaderData.concept;
    const canonical = `https://smartsustainability.ai/concepts/${params.slug}`;
    return {
      meta: [
        { title: pageTitle(`${c.name} — Concept`, "en") },
        { name: "description", content: c.oneLine },
        { property: "og:title", content: c.name },
        { property: "og:description", content: c.oneLine },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "tr_TR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: c.name },
      { name: "twitter:description", content: c.oneLine },
      ],
      links: [
        { rel: "canonical", href: canonical },
        ...enHreflangLinks(
          canonical,
          pilotIdForConcept(params.slug)
            ? `https://akillisurdurulebilirlik.com/sozluk/${pilotIdForConcept(params.slug)}`
            : undefined,
        ),
      ],

      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(personSchema()),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            name: c.name,
            description: c.oneLine,
            inDefinedTermSet: {
              "@type": "DefinedTermSet",
              name: "Smart Sustainability — Concepts",
              url: `${SITE_URL}/concepts`,
            },
            url: canonical,
            inLanguage: "en",
            creator: { "@id": PERSON_ID },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbSchema([
              { name: "Home", item: EN_HOME },
              { name: "Concepts", item: `${SITE_URL}/concepts` },
              { name: c.name, item: canonical },
            ]),
          ),
        },
      ],
    };
  },

  component: ConceptDetail,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-20 text-center">
      <h1 className="font-serif text-3xl text-ink">Concept not found</h1>
      <Link to="/concepts" className="mt-6 inline-block text-[11px] uppercase tracking-[0.2em] text-clay underline underline-offset-4">
        Back to Concepts
      </Link>
    </div>
  ),
});

function ConceptDetail() {
  const { concept: c } = Route.useLoaderData() as { concept: Concept };
  return (
    <article className="mx-auto max-w-3xl px-6 py-16 md:py-20">
      <header className="mb-10">
        <p className="text-[10px] uppercase tracking-[0.24em] text-clay">Concept</p>
        <h1 className="mt-4 font-serif text-4xl leading-tight text-ink md:text-5xl">{c.name}</h1>
        <p className="mt-5 font-serif italic text-lg text-clay md:text-xl leading-snug">
          {c.oneLine}
        </p>
        <p className="mt-4 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          Last updated {new Date(c.updated).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </p>
      </header>

      <MachineSummary
        kind="concept"
        slug={c.slug}
        summary={c.oneLine}
        coreQuestion={`What does ${c.name} name, and why does it matter?`}
        coreClaim={c.whyItMatters}
        relatedConceptSlugs={c.relatedConcepts}
        relatedIdeaSlugs={c.relatedIdeas}
        topics={c.topics}
        updated={c.updated}
      />



      <section className="mb-10">
        <h2 className="mb-3 font-serif text-2xl text-ink">Definition</h2>
        <p className="text-[16px] leading-[1.7] text-foreground/85">{c.definition}</p>
      </section>

      <section className="mb-10">
        <h2 className="mb-3 font-serif text-2xl text-ink">Why it matters</h2>
        <p className="text-[16px] leading-[1.7] text-foreground/85">{c.whyItMatters}</p>
      </section>

      <section className="mb-10">
        <h2 className="mb-3 font-serif text-2xl text-ink">How it appears in AI systems</h2>
        <p className="text-[16px] leading-[1.7] text-foreground/85">{c.howItAppears}</p>
      </section>

      {c.examples.length > 0 && (
        <section className="mb-10">
          <h2 className="mb-3 font-serif text-2xl text-ink">Examples</h2>
          <ul className="space-y-3 list-disc list-outside pl-6 text-[15px] leading-relaxed text-foreground/85">
            {c.examples.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </section>
      )}

      <section className="mb-10">
        <TopicList topics={c.topics} />
      </section>

      {c.relatedConcepts.length > 0 && (
        <RelatedList
          title="Related concepts"
          items={c.relatedConcepts
            .map((slug) => concepts.find((x) => x.slug === slug))
            .filter((x): x is NonNullable<typeof x> => Boolean(x))
            .map((x) => ({ href: `/concepts/${x.slug}`, title: x.name, subtitle: x.oneLine }))}
        />
      )}

      {c.relatedIdeas.length > 0 && (
        <RelatedList
          title="Related ideas"
          items={c.relatedIdeas
            .map((slug) => ideaBySlug(slug))
            .filter((x): x is NonNullable<typeof x> => Boolean(x))
            .map((x) => ({ href: `/ideas/${x.slug}`, title: x.title, subtitle: x.subtitle }))}
        />
      )}

      {c.relatedResearch.length > 0 && (
        <RelatedList
          title="Related research"
          items={c.relatedResearch
            .map((slug) => researchBySlug(slug))
            .filter((x): x is NonNullable<typeof x> => Boolean(x))
            .map((x) => ({ href: `/research/${x.slug}`, title: x.title, subtitle: `${x.institution} · ${x.year}` }))}
        />
      )}

      <CitationBox year={new Date(c.updated).getFullYear()} title={`${c.name} (Concept)`} path={`/concepts/${c.slug}`} />

      <BriefingCTA variant="inline" />
    </article>
  );
}

function RelatedList({ title, items }: { title: string; items: Array<{ href: string; title: string; subtitle: string }> }) {
  return (
    <section className="mb-8 rounded-md border border-ink/10 bg-paper p-6">
      <h2 className="mb-4 font-serif text-lg text-ink">{title}</h2>
      <ul className="space-y-3">
        {items.map((it) => (
          <li key={it.href}>
            <a href={it.href} className="group block">
              <p className="font-serif text-base text-ink group-hover:text-clay">{it.title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{it.subtitle}</p>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
